import React, { useEffect, useRef, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { HiBars3 } from "react-icons/hi2"
import { createPortal } from "react-dom"

const UserRowActionsMenu = ({ actions = [] }) => {
    const [open, setOpen] = useState(false)
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 })
    const wrapperRef = useRef(null)
    const buttonRef = useRef(null)

    const updateMenuPosition = () => {
        if (!buttonRef.current) return
        const rect = buttonRef.current.getBoundingClientRect()
        const menuWidth = 220
        const menuHeight = actions.filter(Boolean).length * 52 + 16 // approx height per item + padding
        const viewportPadding = 16
        const left = Math.max(viewportPadding, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - viewportPadding))

        // Flip upward if not enough space below
        const spaceBelow = window.innerHeight - rect.bottom - viewportPadding
        const top = spaceBelow >= menuHeight
            ? rect.bottom + 10
            : Math.max(viewportPadding, rect.top - menuHeight - 10)

        setMenuPosition({ top, left })
    }

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setOpen(false)
            }
        }

        document.addEventListener("mousedown", handleOutsideClick)
        return () => document.removeEventListener("mousedown", handleOutsideClick)
    }, [])

    useEffect(() => {
        if (!open) return

        updateMenuPosition()

        const handleViewportChange = () => updateMenuPosition()
        window.addEventListener("scroll", handleViewportChange, true)
        window.addEventListener("resize", handleViewportChange)

        return () => {
            window.removeEventListener("scroll", handleViewportChange, true)
            window.removeEventListener("resize", handleViewportChange)
        }
    }, [open])

    const visibleActions = actions.filter(Boolean)
    const visibleActionsCount = visibleActions.length

    return (
        <div className="relative" ref={wrapperRef}>
            <button
                ref={buttonRef}
                type="button"
                className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border border-borderColor bg-white text-tColor shadow-sm transition hover:border-primeColor hover:bg-primeSoft hover:text-primeColor ${open ? "border-primeColor bg-primeSoft text-primeColor" : ""}`}
                aria-label="Open row actions"
                onClick={() => {
                    updateMenuPosition()
                    setOpen((prev) => !prev)
                }}
            >
                <HiBars3 className="text-lg" />
            </button>

            {typeof document !== "undefined" && createPortal(
                <AnimatePresence>
                    {open && (
                        <motion.div
                            initial={{ opacity: 0, y: 8, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 6, scale: 0.98 }}
                            transition={{ duration: 0.16 }}
                            className="fixed z-[140] min-w-[220px] overflow-hidden rounded-2xl border border-borderColor bg-white p-2 shadow-2xl"
                            style={{ top: menuPosition.top, left: menuPosition.left }}
                        >
                            <div className="flex flex-col gap-1">
                                {visibleActions.map((action) => {
                                    const Icon = action.icon
                                    return (
                                        <button
                                            key={action.label}
                                            type="button"
                                            className={`flex items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold transition ${action.danger ? "text-red-700 hover:bg-red-50" : "text-tColor hover:bg-lightGrey/70 hover:text-primeColor"}`}
                                            onClick={() => {
                                                setOpen(false)
                                                action.onClick?.()
                                            }}
                                        >
                                            {Icon && <Icon className="shrink-0 text-base" />}
                                            <span>{action.label}</span>
                                        </button>
                                    )
                                })}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </div>
    )
}

export default UserRowActionsMenu
